import { clamp, type Vec2 } from './math.js';

export interface CircleObstacle {
  kind: 'circle';
  x: number;
  z: number;
  r: number;
}

/**
 * Oriented box on the XZ plane. `hw`/`hd` are half extents along the box's local X and Z axes.
 * `rot` is the yaw (rotation about +Y) using the same convention as Three.js `rotation.y`:
 *   worldX =  localX * cos(rot) + localZ * sin(rot)
 *   worldZ = -localX * sin(rot) + localZ * cos(rot)
 */
export interface BoxObstacle {
  kind: 'box';
  x: number;
  z: number;
  hw: number;
  hd: number;
  rot: number;
}

export type Obstacle = CircleObstacle | BoxObstacle;

export interface Bounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

interface PreparedBox extends BoxObstacle {
  cos: number;
  sin: number;
}

type Prepared = CircleObstacle | PreparedBox;

const CELL = 8;

function boundingRadius(o: Obstacle): number {
  return o.kind === 'circle' ? o.r : Math.sqrt(o.hw * o.hw + o.hd * o.hd);
}

/**
 * Static collision world used identically by client prediction and server simulation.
 * Obstacles are bucketed into a uniform spatial hash for fast circle queries.
 */
export class CollisionWorld {
  readonly bounds: Bounds;
  private readonly obstacles: Prepared[];
  private readonly cells = new Map<number, number[]>();
  private readonly stamp: Uint32Array;
  private queryId = 0;

  constructor(bounds: Bounds, obstacles: readonly Obstacle[]) {
    this.bounds = bounds;
    this.obstacles = obstacles.map((o) =>
      o.kind === 'box' ? { ...o, cos: Math.cos(o.rot), sin: Math.sin(o.rot) } : { ...o },
    );
    this.stamp = new Uint32Array(this.obstacles.length);
    this.obstacles.forEach((o, i) => {
      const br = boundingRadius(o);
      const x0 = Math.floor((o.x - br) / CELL);
      const x1 = Math.floor((o.x + br) / CELL);
      const z0 = Math.floor((o.z - br) / CELL);
      const z1 = Math.floor((o.z + br) / CELL);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cz = z0; cz <= z1; cz++) {
          const key = cellKey(cx, cz);
          let list = this.cells.get(key);
          if (!list) {
            list = [];
            this.cells.set(key, list);
          }
          list.push(i);
        }
      }
    });
  }

  get obstacleCount(): number {
    return this.obstacles.length;
  }

  private forEachNear(x: number, z: number, r: number, fn: (o: Prepared) => boolean | void): void {
    this.queryId = (this.queryId + 1) >>> 0;
    if (this.queryId === 0) {
      this.stamp.fill(0);
      this.queryId = 1;
    }
    const qid = this.queryId;
    const x0 = Math.floor((x - r) / CELL);
    const x1 = Math.floor((x + r) / CELL);
    const z0 = Math.floor((z - r) / CELL);
    const z1 = Math.floor((z + r) / CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const list = this.cells.get(cellKey(cx, cz));
        if (!list) continue;
        for (const i of list) {
          if (this.stamp[i] === qid) continue;
          this.stamp[i] = qid;
          if (fn(this.obstacles[i] as Prepared) === true) return;
        }
      }
    }
  }

  /** True if a circle at (x, z) with radius r overlaps any obstacle or leaves the world bounds. */
  overlaps(x: number, z: number, r: number): boolean {
    const b = this.bounds;
    if (x - r < b.minX || x + r > b.maxX || z - r < b.minZ || z + r > b.maxZ) return true;
    let hit = false;
    this.forEachNear(x, z, r, (o) => {
      if (penetration(o, x, z, r) !== null) {
        hit = true;
        return true;
      }
      return false;
    });
    return hit;
  }

  /**
   * Pushes a circle out of all overlapping obstacles (a few relaxation passes) and clamps it to the bounds.
   * Moving into a wall at an angle therefore slides along it.
   */
  resolve(x: number, z: number, r: number, iterations = 4): Vec2 {
    let px = x;
    let pz = z;
    for (let it = 0; it < iterations; it++) {
      let moved = false;
      this.forEachNear(px, pz, r, (o) => {
        const p = penetration(o, px, pz, r);
        if (p) {
          px += p.x;
          pz += p.z;
          moved = true;
        }
      });
      if (!moved) break;
    }
    const b = this.bounds;
    px = clamp(px, b.minX + r, b.maxX - r);
    pz = clamp(pz, b.minZ + r, b.maxZ - r);
    return { x: px, z: pz };
  }

  /** Conservative swept test: samples the segment every `step` metres. */
  segmentClear(ax: number, az: number, bx: number, bz: number, r: number, step = 0.4): boolean {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.sqrt(dx * dx + dz * dz);
    const n = Math.max(1, Math.ceil(len / step));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      if (this.overlaps(ax + dx * t, az + dz * t, r)) return false;
    }
    return true;
  }
}

function cellKey(cx: number, cz: number): number {
  return (cx + 4096) * 8192 + (cz + 4096);
}

/** Returns the push-out vector for a circle overlapping obstacle `o`, or null if they do not overlap. */
function penetration(o: Prepared, x: number, z: number, r: number): Vec2 | null {
  if (o.kind === 'circle') {
    const dx = x - o.x;
    const dz = z - o.z;
    const minD = r + o.r;
    const d2 = dx * dx + dz * dz;
    if (d2 >= minD * minD) return null;
    const d = Math.sqrt(d2);
    if (d < 1e-6) return { x: minD, z: 0 };
    const push = minD - d;
    return { x: (dx / d) * push, z: (dz / d) * push };
  }
  // Into box-local space (inverse rotation).
  const wx = x - o.x;
  const wz = z - o.z;
  const lx = wx * o.cos - wz * o.sin;
  const lz = wx * o.sin + wz * o.cos;
  const cx = clamp(lx, -o.hw, o.hw);
  const cz = clamp(lz, -o.hd, o.hd);
  let px: number;
  let pz: number;
  const dx = lx - cx;
  const dz = lz - cz;
  const d2 = dx * dx + dz * dz;
  if (d2 > 1e-12) {
    if (d2 >= r * r) return null;
    const d = Math.sqrt(d2);
    const push = r - d;
    px = (dx / d) * push;
    pz = (dz / d) * push;
  } else {
    // Centre is inside the box: exit through the nearest face.
    const ox = o.hw - Math.abs(lx);
    const oz = o.hd - Math.abs(lz);
    if (ox < oz) {
      px = (ox + r) * (lx >= 0 ? 1 : -1);
      pz = 0;
    } else {
      px = 0;
      pz = (oz + r) * (lz >= 0 ? 1 : -1);
    }
  }
  // Back to world space.
  return { x: px * o.cos + pz * o.sin, z: -px * o.sin + pz * o.cos };
}
